import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetListOfSessionsProps, internetApi } from '../../../../services/apiServices/user/internet';
import { AccountTypes } from '../../../../models/Common';
import { OnlineTime, SessionItem } from '../../../../models/Sessions';
import DateVO from '../../../../core/valueObjects/DateVO';
import { PaginationTable } from '../../../commons/customTables/PaginationTable';
import { useDateLocalization } from '../../../../hooks/useDateLocalization';
import { CustomTable } from '../../../../models/CustomTable';
import { SessionsYearSelector } from './SessionsYearSelector';
import { currentYear } from '../../../../constants/configuration';
import { SessionRowProps } from '../Sessions';
import { getMonthFromString, getNumberOfLastDayOfMonth } from '../../../../utils';
import SessionsMonthSelector from './SessionsMonthSelector/SessionsMonthSelector';

interface ArchiveSessionsProps {
  accountId: number | string;
  accountType: AccountTypes | string;
  isUnion: boolean;
  formatOnlineTime: (time: OnlineTime) => string;
  createRow: (props: SessionRowProps) => {
    bodyCells: string[];
    isCollapsibleButton: boolean;
    id: string | number;
    history: JSX.Element;
  };
}

export const ArchiveSessions: FC<ArchiveSessionsProps> = ({
  accountId,
  accountType,
  isUnion,
  formatOnlineTime,
  createRow,
}) => {
  const { t } = useTranslation();
  const { formatDate } = useDateLocalization();
  const [currentPage, setCurrentPage] = useState(0);
  const [disableMonthBtn, setDisableMonthBtn] = useState(true);
  const [sessionsPerPage, setSessionsPerPage] = useState(5);
  const [selectedYear, setSelectedYear] = useState<string>('for all years');
  const [selectedMonth, setSelectedMonth] = useState<string>('for all months');
  const scrollToTop = useRef<HTMLDivElement | null>(null);

  const [queryParams, setQueryParams] = useState<GetListOfSessionsProps>({
    accountId,
    perPage: sessionsPerPage,
    page: currentPage + 1,
  });
  const { data: listOfSessions, refetch: reFetchListOfSessions } = internetApi.useGetListOfSessionsQuery(queryParams, {
    skip: accountType !== AccountTypes.Internet && !isUnion,
  });

  useEffect(() => {
    reFetchListOfSessions();
  }, [queryParams]);

  useEffect(() => {
    setQueryParams({ ...queryParams, page: currentPage + 1 });
  }, [currentPage]);

  useEffect(() => {
    setQueryParams({ ...queryParams, perPage: sessionsPerPage });
  }, [sessionsPerPage]);

  const handleDateParams = () => {
    const yearIsNan = Number.isNaN(+selectedYear);
    const monthIsNan = Number.isNaN(+selectedMonth);
    if (yearIsNan && monthIsNan) {
      setQueryParams({ ...queryParams, dateFrom: undefined, dateTo: undefined });
    }
    if (!yearIsNan && monthIsNan) {
      setQueryParams({ ...queryParams, dateFrom: `${selectedYear}-01-01`, dateTo: `${selectedYear}-12-31` });
      setCurrentPage(0);
    }
    if (!yearIsNan && !monthIsNan) {
      setQueryParams({
        ...queryParams,
        dateFrom: `${selectedYear}-${getMonthFromString(selectedMonth)}-01`,
        dateTo:
          +selectedMonth !== 12
            ? `${selectedYear}-${getMonthFromString(selectedMonth)}-${getNumberOfLastDayOfMonth(
                selectedYear,
                selectedMonth,
              )}`
            : `${+selectedYear + 1}-01-01`,
      });
      setCurrentPage(0);
    }
    /* возвращаем дефолтное значение, так как АПИ не возвращает только выбранные месяца по всем годам */
    if (yearIsNan && !monthIsNan) {
      setQueryParams({ ...queryParams, dateFrom: undefined, dateTo: undefined });
    }
  };

  useEffect(() => {
    handleDateParams();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (!Number.isNaN(+selectedYear)) {
      setDisableMonthBtn(false);
    } else {
      setSelectedMonth('for all months');
      setDisableMonthBtn(true);
    }
  }, [selectedYear]);

  const archiveSessionsList = listOfSessions?.data?.sessions || [];

  const archiveSessions: CustomTable = {
    tableHead: [
      `${t('serviceManagement.start')}`,
      `${t('serviceManagement.end')}`,
      `${t('serviceManagement.onlineTime')}`,
    ],
    tableBodyCells:
      archiveSessionsList &&
      archiveSessionsList.map((session: SessionItem) => {
        return createRow({
          id: session.id,
          start: formatDate(DateVO.create(session.InDate || '').get(), {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            locale: 'uk',
          }),
          onlineTime: formatOnlineTime(session.OnlineTime),
          ipv4: `${session.Outer_IP.ipv4}`,
          ipv4Sent: `${session.Sent.ipv4}`,
          ipv4Received: `${session.Received.ipv4}`,
          ipv6: `${session.Outer_IP.ipv6}`,
          ipv6Sent: `${session.Sent.ipv6}`,
          ipv6Received: `${session.Received.ipv6}`,
          end: formatDate(DateVO.create(session.OutDate || '').get(), {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            locale: 'uk',
          }),
        });
      }),
  };

  const handleYearSelect = (year: string) => {
    setSelectedYear(year);
  };

  const handleMonthSelect = (month: string) => {
    setSelectedMonth(month);
  };

  return (
    <div className="archive-sessions-container">
      <div className="archive-sessions-container__selectors" ref={scrollToTop}>
        <SessionsMonthSelector
          disabled={disableMonthBtn}
          monthsOptions={selectedMonth}
          handleMonthSelect={handleMonthSelect}
        />
        <SessionsYearSelector
          yearsOptions={selectedYear}
          handleYearSelect={handleYearSelect}
          startYear={listOfSessions?.data?.startYear || currentYear}
        />
      </div>
      {archiveSessionsList.length ? (
        <PaginationTable
          tableHead={archiveSessions.tableHead}
          tableBodyCells={archiveSessions.tableBodyCells}
          isCollapsibleButton
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalRowsCount={listOfSessions?.total_pages || 0}
          isLazyTable
          rowsPerPage={sessionsPerPage}
          setRowsPerPage={setSessionsPerPage}
          toTop={scrollToTop}
        />
      ) : (
        <p className="label">{t('serviceManagement.youHaveNoArchiveSessions')}</p>
      )}
    </div>
  );
};
